import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { userGroupsService } from '@modules/userGroups/services/userGroupsService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export const useFormGroup = (userGroup, selectedData, onClose, fetchData, t) => {
    const [serverMessage, setServerMessage] = useState(null);

    const validationSchema = yup.object({
        name: yup.string()
            .required(t('nav.userGroups.validation.required'))
            .min(1, t('nav.userGroups.validation.nameMinLength'))
            .max(50, t('nav.userGroups.validation.nameMaxLength')),
        ldapGroupId: yup.number()
            .required(t('nav.userGroups.validation.required'))
            .typeError(t('nav.userGroups.validation.ldapGroupId'))
            .nullable()
            .transform((value) => (isNaN(value) ? null : value))
    });

    const { register, handleSubmit, formState: { errors }, setValue } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: userGroup
            ? {
                id: userGroup.id,
                name: userGroup.name || '',
                ldapGroupId: userGroup.ldapGroupId || null,
                isAdministrative: userGroup.isAdministrative || false,
                specRights: userGroup.specRights || [],
                users: userGroup.users || []
            }
            : {
                name: '',
                ldapGroupId: null,
                isAdministrative: false,
                specRights: [],
                users: []
            }
    });

    const handleUpdateGroup = useCallback(async (data) => {
        try {
            await userGroupsService.updateGroup(selectedData, data);
            notifySuccess(t('nav.userGroups.notifications.updateSuccess'));
            return true;
        } catch (error) {
            setServerMessage(error?.response?.data?.message);
            notifyError(t('nav.userGroups.notifications.updateError'));
            return false;
        }
    }, [selectedData, t]);

    const handleCreateGroup = useCallback(async (data) => {
        try {
            await userGroupsService.createGroup(data);
            notifySuccess(t('nav.userGroups.notifications.createSuccess'));
            return true;
        } catch (error) {
            setServerMessage(error?.response?.data?.message);
            notifyError(t('nav.userGroups.notifications.createError'));
            return false;
        }
    }, [t]);

    const onSubmit = useCallback(async (data) => {
        try {
            const success = userGroup
                ? await handleUpdateGroup(data)
                : await handleCreateGroup(data);

            if (success) {
                fetchData(userGroupsService.getGroups, {});
                onClose();
            }
        } catch (error) {
            console.error('Failed to submit group:', error);
            setServerMessage(error?.message || t('userGroups.notifications.submitError'));
        }
    }, [userGroup, handleUpdateGroup, handleCreateGroup, fetchData, onClose, t]);

    return {
        register,
        handleSubmit,
        errors,
        setValue,
        serverMessage,
        validationSchema,
        onSubmit
    };
};